import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { parse as parseParams } from 'hub-http/helpers/params';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import unescapedText from 'I18n/utils/unescapedText';

// @ts-expect-error untyped dependency
import { HR } from 'UIComponents/elements';
import { H5 } from 'UIComponents/elements/headings';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import UITile from 'UIComponents/tile/UITile';
import UIButton from 'UIComponents/button/UIButton';
import UILink from 'UIComponents/link/UILink';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISelect from 'UIComponents/input/UISelect';
import { ReviewWizardRatingInput, ReviewWizardTextInput } from 'trellis-ecosystem';
import { PENDING } from 'marketplace-ui-reviews/constants/UnreviewableReason';
import { EcosystemReviewWizard } from 'marketplace-ui-reviews/components/wizard/EcosystemReviewWizard';
import { POSITIVE_RATING_MINIMUM } from 'marketplace-ui-reviews/constants/ReviewLimits';
// @ts-expect-error untyped dependency
import { getIsPublic } from '../../../selectors/auth';
import { AppReviewWizardHeader } from './AppReviewWizardHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DETAILED_RATING_AXES = ['VALUE', 'USABILITY', 'FUNCTIONALITY', 'SUPPORT_DOCS'];
const EDIT_REASONS = ['MISSPELLING', 'NEW_INFORMATION', 'ISSUE_RESOLVED', 'APPROACHED_BY_PARTNER', 'OTHER'];
function getCompletedAnswerFields(review) {
  return Object.entries(review.content).filter(([field, value]) => field && !!value).map(([field]) => field);
}
function getDefaultReview(isEditing, slug, parsedRating, editedReview) {
  if (isEditing && editedReview) {
    const {
      answers,
      ratings
    } = editedReview.toJSON();
    return {
      content: answers,
      ratings
    };
  }
  const emptyReview = {
    content: {},
    ratings: {
      OVERALL: parsedRating
    }
  };
  try {
    const storedReview = JSON.parse(sessionStorage.getItem(`${slug}_review`));
    return storedReview || emptyReview;
  } catch (__error) {
    return emptyReview;
  }
}
export function AppReviewWizard({
  appIconUrl,
  appName,
  editedReview,
  offeringId,
  onClose,
  onSuccess,
  onTrackInteraction,
  slug,
  supportLink,
  unreviewableReason
}) {
  const isPublic = useSelector(getIsPublic);
  const {
    search
  } = useLocation();
  const {
    eco_review_source,
    rating
  } = parseParams(search.slice(1));
  const reviewSource = eco_review_source || 'details';
  const parsedRating = /^[1-5]$/.test(rating) ? parseInt(rating, 10) : undefined;
  const isEditing = !!editedReview;
  const [editReason, setEditReason] = useState('');
  const defaultReview = useMemo(() => getDefaultReview(isEditing, slug, parsedRating, editedReview), [editedReview, isEditing, parsedRating, slug]);
  const [originalReview] = useState(getDefaultReview(isEditing, slug, parsedRating, editedReview));
  const [hasEdits, setHasEdits] = useState(false);
  const [overallRating, setOverallRating] = useState(defaultReview.ratings['OVERALL'] || undefined);
  const [completedAnswerFields, setCompletedAnswerFields] = useState(getCompletedAnswerFields(defaultReview));
  const [hasDetailedRatings, setHasDetailedRatings] = useState(Object.keys(defaultReview.ratings).length > 1);
  const [showDetailedRatings, setShowDetailedRatings] = useState(hasDetailedRatings);
  const [requireFields, setRequireFields] = useState(overallRating !== undefined && overallRating < POSITIVE_RATING_MINIMUM || !!defaultReview.content.TITLE || !!defaultReview.content.REVIEW || !!defaultReview.content.PROS || !!defaultReview.content.CONS);
  const validationSchema = {
    content: {
      TITLE: {
        required: requireFields
      },
      REVIEW: {
        required: requireFields
      }
    },
    ratings: {
      OVERALL: {
        required: true
      }
    }
  };
  let disableOverallRating = false;
  if (isEditing) {
    const editableDate = new Date(editedReview.createdAt);
    editableDate.setDate(editableDate.getDate() + 14);
    disableOverallRating = editableDate < new Date();
  }
  useEffect(() => {
    onTrackInteraction('open review wizard', {
      reviewSource,
      section: 'review'
    });
  }, [onTrackInteraction, reviewSource]);
  useEffect(() => {
    if (!isEditing && unreviewableReason && unreviewableReason !== `ReviewError.${PENDING}`) {
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.error.${unreviewableReason}`
        }),
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.reviews.unreviewable.titleText"
        }),
        type: 'danger'
      });
    }
  }, [isEditing, unreviewableReason]);
  const handleSuccess = useCallback(() => {
    onTrackInteraction('submit review', {
      reviewSource,
      section: 'review'
    });
    sessionStorage.removeItem(`${slug}_review`);
    onSuccess();
  }, [onSuccess, onTrackInteraction, reviewSource, slug]);
  const handleClose = useCallback(() => {
    onTrackInteraction('dismiss review wizard', {
      reviewSource,
      section: 'review'
    });
    onClose();
  }, [onClose, onTrackInteraction, reviewSource]);
  const onChange = useCallback(review => {
    let hasChanges = false;
    Object.entries(review).forEach(([typeKey, typeObj]) => {
      Object.entries(typeObj).forEach(([key, value]) => {
        if (!originalReview[typeKey][key] && value) {
          hasChanges = true;
        } else if (value && originalReview[typeKey][key] !== value) {
          hasChanges = true;
        }
      });
    });
    setHasEdits(hasChanges);
    if (review.content.TITLE || review.content.REVIEW || review.content.PROS || review.content.CONS) {
      setRequireFields(true);
    } else {
      setRequireFields(review.ratings['OVERALL'] < POSITIVE_RATING_MINIMUM);
    }
    if (Object.keys(review.ratings).length > 1 && !hasDetailedRatings) {
      onTrackInteraction('add detailed rating', {
        section: 'review'
      });
      setHasDetailedRatings(true);
    }
    if (review.ratings['OVERALL'] !== overallRating) {
      onTrackInteraction('set overall rating', {
        section: 'review',
        value: review.ratings['OVERALL']
      });
      setOverallRating(review.ratings['OVERALL']);
    }
    const answerFields = getCompletedAnswerFields(review);
    answerFields.forEach(field => {
      if (!completedAnswerFields.includes(field)) {
        onTrackInteraction('add review answer', {
          section: 'review',
          value: field
        });
      }
    });
    setCompletedAnswerFields(answerFields);
    if (!isEditing) {
      sessionStorage.setItem(`${slug}_review`, JSON.stringify(review));
    }
  }, [completedAnswerFields, hasDetailedRatings, isEditing, onTrackInteraction, originalReview, overallRating, slug]);
  const reviewFormatter = useCallback(review => {
    const filteredReview = Object.assign({}, review);
    if (!showDetailedRatings) {
      DETAILED_RATING_AXES.forEach(axis => delete filteredReview.ratings[axis]);
    }
    return filteredReview;
  }, [showDetailedRatings]);
  const WizardHeader = useMemo(() => () => /*#__PURE__*/_jsx(AppReviewWizardHeader, {
    appIconUrl: appIconUrl,
    appName: appName
  }), [appIconUrl, appName]);
  if (isPublic || !isEditing && unreviewableReason) {
    return null;
  }
  const customValidationErrors = [];
  if (isEditing && !hasEdits) {
    customValidationErrors.push(unescapedText('appMarketplace.detail.reviews.wizard.validation.noEdits'));
  }
  if (isEditing && !editReason) {
    customValidationErrors.push(unescapedText('appMarketplace.detail.reviews.wizard.validation.noReason'));
  }
  return /*#__PURE__*/_jsxs(EcosystemReviewWizard, {
    customValidationErrors: customValidationErrors,
    defaultReview: defaultReview,
    HeaderComponent: WizardHeader,
    isEditing: isEditing,
    editReason: editReason,
    offeringId: offeringId,
    onChange: onChange,
    onClose: handleClose,
    onSuccess: handleSuccess,
    reviewFormatter: reviewFormatter,
    reviewId: editedReview && editedReview.get('id'),
    reviewType: "APP",
    validationSchema: validationSchema,
    children: [/*#__PURE__*/_jsxs("span", {
      className: "m-bottom-4",
      children: [/*#__PURE__*/_jsx(H5, {
        className: "m-bottom-2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.reviews.wizard.header"
        })
      }), supportLink && /*#__PURE__*/_jsx(FormattedJSXMessage, {
        elements: {
          Link: UILink
        },
        message: "appMarketplace.detail.reviews.wizard.subheader_jsx",
        options: {
          appName,
          external: true,
          href: supportLink,
          onClick: () => {
            onTrackInteraction('click support link', {
              section: 'review'
            });
          }
        }
      })]
    }), /*#__PURE__*/_jsx(ReviewWizardRatingInput, {
      disabled: disableOverallRating,
      fieldKey: "OVERALL",
      label: unescapedText('appMarketplace.detail.reviews.wizard.labels.OVERALL')
    }), !!overallRating && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        onClick: () => {
          onTrackInteraction(showDetailedRatings ? 'hide detailed ratings' : 'show detailed ratings', {
            section: 'review'
          });
          if (isEditing && showDetailedRatings && Object.keys(originalReview.ratings).length > 1) {
            setHasEdits(true);
          }
          setShowDetailedRatings(!showDetailedRatings);
        },
        use: "link",
        children: !showDetailedRatings ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.reviews.wizard.detailedRatings.show"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.reviews.wizard.detailedRatings.hide"
        })
      }), showDetailedRatings && /*#__PURE__*/_jsx(UITile, {
        className: "p-bottom-4 p-left-4",
        children: DETAILED_RATING_AXES.map(axis => /*#__PURE__*/_jsx(ReviewWizardRatingInput, {
          fieldKey: axis,
          label: unescapedText(`appMarketplace.detail.reviews.wizard.labels.${axis}`),
          tooltip: unescapedText(`appMarketplace.detail.reviews.wizard.tooltips.${axis}`),
          type: "pill"
        }, axis))
      }), isEditing && /*#__PURE__*/_jsx(UIFormControl, {
        label: unescapedText('appMarketplace.detail.reviews.wizard.labels.editReason'),
        required: true,
        children: /*#__PURE__*/_jsx(UISelect, {
          onChange: ({
            target: {
              value
            }
          }) => {
            setEditReason(value);
          },
          options: EDIT_REASONS.map(reason => ({
            value: reason,
            text: unescapedText(`appMarketplace.detail.reviews.wizard.editReasons.${reason}`)
          }))
        })
      }), /*#__PURE__*/_jsx(ReviewWizardTextInput, {
        fieldKey: "TITLE",
        label: unescapedText('appMarketplace.detail.reviews.wizard.labels.TITLE'),
        resize: false,
        rows: 1,
        placeholder: unescapedText('appMarketplace.detail.reviews.wizard.placeholders.TITLE')
      }), ['REVIEW', 'PROS', 'CONS'].map(axis => /*#__PURE__*/_jsx(ReviewWizardTextInput, {
        fieldKey: axis,
        label: unescapedText(`appMarketplace.detail.reviews.wizard.labels.${axis}`),
        placeholder: unescapedText(`appMarketplace.detail.reviews.wizard.placeholders.${axis}`, {
          appName
        })
      }, axis)), !isEditing && /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(HR, {
          className: "m-top-8"
        }), /*#__PURE__*/_jsx(ReviewWizardTextInput, {
          fieldKey: "FEEDBACK",
          label: unescapedText('appMarketplace.detail.reviews.wizard.labels.FEEDBACK'),
          placeholder: unescapedText('appMarketplace.detail.reviews.wizard.placeholders.FEEDBACK', {
            appName
          })
        })]
      })]
    })]
  });
}